<template>
  <div>
    <div class="result-title">
      <div v-if="listing.length > 0 && isAllUpdateSuccess">
        <span class="success"><font-awesome-icon icon="check-circle" /> </span>
        {{ $t('batchUpdateResults.listing.successMessage', { tolObject }) }}
      </div>
      <div v-else-if="listing.length > 0 && !isAllUpdateSuccess">
        <span class="error"><font-awesome-icon icon="exclamation-circle" /> </span>
        {{ $t('batchUpdateResults.listing.errorMessage', { tolObject }) }}
      </div>
      <div v-else>
        <span class="return"><font-awesome-icon icon="question-circle" /> </span
        >{{ $t('batchUpdateResults.listing.returnMessage') }}
      </div>
    </div>
    <update-modal v-if="pdfGenerationLoading">
      <template v-slot:modalData>
        <vue-simple-spinner size="massive" />
      </template>
    </update-modal>
    <tol-table
      v-if="tolObject"
      :table-data="listing"
      :columns="columns[tolObject]"
      :is-row-clickable="true"
      @row-click="handleBatchResultClicked($event)"
    >
      <template v-slot:tableHeader>
        <th></th>
        <!-- empty header cell to accommodate checkbox -->
        <template v-for="(item, index) in columns[tolObject]">
          <th :key="index">{{ item | readable | camelCase }}</th>
        </template>
      </template>
      <template v-slot:check="{ value }">
        <td class="row-select" @click.stop>
          <Checkbox
            :checked="selectedBatchResults.includes(value.reference_number)"
            @change="onToggleSelectBatchResult(value.reference_number)"
          />
        </td>
      </template>
      <template v-slot:name="{ value }">
        {{ value.last_name }}, {{ value.first_name }}
      </template>
      <template v-slot:start_date="{ value }">
        <font-awesome-icon icon="calendar-alt" />
        {{ value.start_date }}
      </template>

      <template v-slot:end_date="{ value }">
        <font-awesome-icon icon="calendar-alt" />
        {{ value.end_date }}
      </template>
      <template v-slot:message="{ value }">
        <ul>
          <li v-for="(item, index) in value.message" :key="index" class="msg">
            {{ item | readable }}
          </li>
        </ul>
      </template>

      <template v-slot:status="{ value }">
        <Status :status="value.status" object-type="assignment" />
      </template>
    </tol-table>
    <div class="result-action">
      <div class="select-all">
        <label>All/None</label>
        <Checkbox :checked="isAllSelected" @change="onToggleAll" />
      </div>

      <TolAltButton
        label="batch pdf"
        icon-name="file-pdf"
        :disabled="selectedBatchResults.length === 0"
        @click="printBatchPdf()"
      />
    </div>
    <VueHtml2pdf
      ref="BatchDownloadComp"
      :show-layout="false"
      :paginate-elements-by-height="1400"
      pdf-format="a4"
      :manual-pagination="false"
      :enable-download="false"
      :preview-modal="true"
      :filename="`Timesheet-Batch`"
    >
      <section slot="pdf-content">
        <TimeSheetBatchPdf @readyToExport="exportAsPdf" />
      </section>
    </VueHtml2pdf>
  </div>
</template>

<script>
import TolTable from '@/app/components/TolTable';
import Status from '@/app/components/Status';
import TolAltButton from '@/app/components/TolAltButton';
import Checkbox from '@/app/components/Checkbox';
import {
  BatchUpdateListingGetters,
  BatchUpdateListingActions
} from '@/app/features/batch-update/batch-update-listing.keys';
import { timeSheetsListingService as timeSheetsService } from '@/app/features/time-sheets/features/time-sheets-listing/time-sheets-listing.service';
import { expensesListingService as expensesService } from '@/app/features/expenses/features/expenses-listing/expenses-listing.service';

import { AppActions } from '@/app/app.keys';
import TimeSheetBatchPdf from '../../features/time-sheets/features/time-sheet-details/components/TimeSheetBatchPdf';
import UpdateModal from '@/app/components/UpdateModal';
export default {
  components: {
    TolTable,
    Status,
    TolAltButton,
    Checkbox,
    TimeSheetBatchPdf,
    UpdateModal
  },

  data() {
    const defaults = [
      'contractor_name',
      'client_name',
      'payroll_number',
      'start_date',
      'status',
      'message'
    ];

    return {
      columns: {
        'Time Sheets': (() => {
          const a = defaults.slice();
          a.splice(4, 0, 'end_date');
          return a;
        })(),
        'Expense Reports': (() => {
          const a = defaults.slice();
          a.splice(4, 0, 'end_date');
          return a;
        })(),
        Assignments: (() => {
          const a = defaults.slice();
          a.splice(4, 0, 'stop_date');
          return a;
        })(),
        'Perm Placements': [
          'invoice_number',
          'name',
          'client_name',
          'start_date',
          'branch_name',
          'status',
          'message'
        ]
      }
    };
  },
  computed: {
    listing() {
      return this.$store.getters[BatchUpdateListingGetters.BatchUpdateResult];
    },
    isAllUpdateSuccess() {
      return this.$store.getters[BatchUpdateListingGetters.IsAllUpdateSuccess];
    },
    tolObject() {
      return this.$store.getters[BatchUpdateListingGetters.BatchUpdateTolObject];
    },
    isAllSelected() {
      return this.$store.getters[BatchUpdateListingGetters.IsAllResultsSelected];
    },
    selectedBatchResults() {
      return this.$store.getters[BatchUpdateListingGetters.SelectedBatchResults];
    },
    pdfGenerationLoading() {
      return this.$store.getters[BatchUpdateListingGetters.IsBatchPdfGenerated];
    }
  },
  mounted() {
    this.$store.dispatch(AppActions.SetPageTitle, this.$t('batchUpdateResults.listing.title'));
  },
  methods: {
    onToggleAll(evt) {
      this.$store.dispatch(BatchUpdateListingActions.SelectDeselectAllResults, evt.checked);
    },
    printBatchPdf() {
      this.$store.dispatch(BatchUpdateListingActions.SetBatchPdfGenerated, true);
      if (this.$route.name === 'expenses-batch-update') {
        expensesService.bulkUpdateExpenses({ action: 'PDF' }, this.selectedBatchResults);
      } else if (this.$route.name === 'time-sheet-batch-update') {
        timeSheetsService.bulkUpdateTimeSheets({ action: 'PDF' }, this.selectedBatchResults);
      }
    },
    onToggleSelectBatchResult(ref) {
      this.$store.dispatch(BatchUpdateListingActions.ToggleSelectBatchResult, ref);
    },
    handleBatchResultClicked() {
      //TODO
      //1. show record detail when row clicked
    },
    exportAsPdf() {
      this.$refs.BatchDownloadComp.generatePdf();
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/app/styles/colors';
@import '@/app/styles/variables';

.result-title {
  display: flex;
  flex: 1;
  padding: 60px;
  background-color: $white;
  div {
    display: flex;
    align-items: center;
  }
  span,
  svg {
    display: inline-block;
    margin-right: 15px;
    width: 30px;
    height: 30px;
  }
  span.success {
    color: $green;
  }
  span.error {
    color: $orange;
  }
}

.result-action {
  display: flex;
  padding: 60px;
  background-color: $white;
  //margin-right: 30px;
  justify-content: space-between;
  .select-all {
    display: flex;
    flex-direction: column;
    text-transform: uppercase;
    font-size: 0.8em;
    align-items: center;
  }
}

.msg {
  font-size: 0.7em;
  color: $red;
}
</style>
